import cn from 'classnames';
import { useRef, type FC } from 'react';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedFeaturedBannerModule.module.css';
import { VisibilityTracker } from 'dibs-visibility-tracker/src/VisibilityTracker';

const banner = {
    image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_12-10-25/bottom_banner.jpg',
    mobileImage:
        'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_12-10-25/bottom_banner.jpg',
    topic: undefined,
    title: (
        <FormattedMessage
            id={`homepage.HpSharedFeaturedBannerModule.title`}
            defaultMessage="Irresistible New Furniture Arrivals"
        />
    ),
    description: (
        <FormattedMessage
            id={`homepage.HpSharedFeaturedBannerModule.description`}
            defaultMessage="Be the first to shop the latest pieces just added to our marketplace."
        />
    ),
    buttonText: (
        <FormattedMessage
            id={`homepage.HpSharedFeaturedBannerModule.buttonText`}
            defaultMessage="Shop Now"
        />
    ),
    link: '/new-arrivals/furniture/?new-and-notable=true',
};

export const HpSharedFeaturedBannerModule: FC<{
    isMobile: boolean;
}> = ({ isMobile }) => {
    const featuredBannerRespRef = useRef(null);
    const featuredBannerMobileRef = useRef(null);

    const promoTrackingObject = buildPromoTrackingObject({
        index: 0,
        id: 'bottom-banner',
        name: 'Bottom Banner',
        item: { linkData: { path: banner.link } },
    });

    let bannerImage = banner.image;
    if (isMobile) {
        bannerImage = banner.mobileImage ? banner.mobileImage : banner.image;
    }

    return isMobile ? (
        <>
            <VisibilityTracker
                elementRef={featuredBannerMobileRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={banner.link}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.flexCol,
                        dibsCss.mbLarge,
                        dibsCss.mtMedium,
                        styles.consumerBg
                    )}
                    ref={featuredBannerMobileRef}
                >
                    <div>
                        <img
                            alt=""
                            sizes="100vw"
                            src={bannerImage}
                            className={cn(styles.mobileImage, dibsCss.wFull)}
                            srcSet={getSrcsetString(bannerImage)}
                        />
                    </div>
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.pxSmall,
                            dibsCss.myMedium,
                            dibsCss.itemsCenter
                        )}
                    >
                        {banner.topic && (
                            <span
                                className={cn(dibsCss.sassyFontHeaderSmallItalic, dibsCss.mbXsmall)}
                            >
                                {banner.topic}
                            </span>
                        )}
                        <span className={cn(dibsCss.sassyFontHeaderSizeLarge, dibsCss.mbSmall)}>
                            {banner.title}
                        </span>
                        <div>
                            <Button size="medium" type="transparent" fullWidth={false}>
                                {banner.buttonText}
                            </Button>
                        </div>
                    </div>
                </div>
            </Link>
        </>
    ) : (
        <>
            <VisibilityTracker
                elementRef={featuredBannerRespRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={banner.link}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    ref={featuredBannerRespRef}
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.relative,
                        dibsCss.myLarger,
                        dibsCss.flexRowReverse,
                        styles.consumerBg
                    )}
                >
                    <img
                        alt=""
                        sizes="50vw"
                        src={bannerImage}
                        className={cn(
                            dibsCss.wFull,
                            dibsCss.hFull,
                            dibsCss.basis50,
                            dibsCss.maxH500px,
                            dibsCss.objectCover
                        )}
                        srcSet={getSrcsetString(bannerImage, [960, 1280, 1600, 1920])}
                    />
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.basis50,
                            dibsCss.pxLarger,
                            dibsCss.gapXsmall,
                            dibsCss.justifyCenter
                        )}
                    >
                        <span className={cn(dibsCss.sassyFontHeaderMediumItalic, dibsCss.mbXsmall)}>
                            {banner.topic}
                        </span>
                        <span
                            className={cn(
                                dibsCss.sassyFontHeaderSizeXLargeTypeNormal,
                                styles.respTitle
                            )}
                        >
                            {banner.title}
                        </span>
                        <span
                            className={cn(
                                dibsCss.sassyFontHeaderLargeNormal,
                                dibsCss.mbXsmall,
                                styles.respDescription
                            )}
                        >
                            {banner.description}
                        </span>
                        <div>
                            <Button size="large" type="transparent" fullWidth={false}>
                                {banner.buttonText}
                            </Button>
                        </div>
                    </div>
                </div>
            </Link>
        </>
    );
};
